import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Feature, translationKeys } from '../../../core';
import { FeatureService, RecentSearch, RecentSearchService, RecentSearchTypeEnum, SearchService } from '../../../services';
import { AvanoLoadingIndicatorService } from '@avano/avano-components';
import { DomSanitizer } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';

@Component({
  selector: 'scip-recent-searches',
  templateUrl: './recent-searches.component.html',
  styleUrls: ['./recent-searches.component.scss']
})
export class RecentSearchesComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() recentSearchesType: RecentSearchTypeEnum;
  recentSearchList: RecentSearch[] = [];
  tempRecentSearchList: RecentSearch[] = [];
  currentSearchTerm: string;

  loading = true;
  blockingIndicator = 'recentSearches';
  translationKeys: typeof translationKeys = translationKeys;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private searchService: SearchService,
    private recentSearchService: RecentSearchService,
    private featureService: FeatureService,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService,
    private changeDet: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadInRecentSearches(this.recentSearchesType);
    this.listenToNewSearches();
  }

  ngAfterViewInit(): void {
    this.avanoLoadingIndicatorService.block([this.blockingIndicator]);
  }

  clickRecentSearch(entry: RecentSearch): void {
    this.currentSearchTerm = '';

    //Send SearchInfo to grid to trigger search again
    if (this.recentSearchesType === RecentSearchTypeEnum.Products) {
      this.searchService.emitProductSearchEvent({ searchId: entry.productLastSearchesDto.id, recentSearch: entry });
    } else if (this.recentSearchesType === RecentSearchTypeEnum.Submissions) {
      this.searchService.emitSubmissionSearchEvent({ searchId: entry.submissionSearchesDto.id, recentSearch: entry });
    }
  }

  filterRecentSearches(): void {
    if (this.currentSearchTerm && this.currentSearchTerm.length) {
      this.recentSearchList = this.tempRecentSearchList.filter((entry) => entry.text.toLowerCase().includes(this.currentSearchTerm.toLowerCase()));
    } else {
      this.recentSearchList = this.tempRecentSearchList;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private listenToNewSearches(): void {
    // We want to reload the list of recent searches when a new search is executed (it could be that the last one, the tenth was deleted and should
    // not be clicked anymore)
    if (this.recentSearchesType === RecentSearchTypeEnum.Products) {
      this.searchService
        .getProductSearchEvents()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.loadInRecentSearches(RecentSearchTypeEnum.Products));
    } else if (this.recentSearchesType === RecentSearchTypeEnum.Submissions) {
      this.searchService
        .getSubmissionSearchEvents()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.loadInRecentSearches(RecentSearchTypeEnum.Submissions));
    }
  }

  private loadInRecentSearches(type: RecentSearchTypeEnum) {
    this.recentSearchService.getLastTenSearches(type, this.featureService.isActive(Feature.LE_GROUPING)).subscribe(
      (data) => {
        this.recentSearchList = data.map((item) => ({
          ...item,
          savedHtml: this.sanitizer.sanitize(SecurityContext.HTML, item.savedHtml)
        }));
        this.tempRecentSearchList = data.map((item) => ({
          ...item,
          savedHtml: this.sanitizer.sanitize(SecurityContext.HTML, item.savedHtml)
        }));
        this.loading = false;
        this.avanoLoadingIndicatorService.unblock([this.blockingIndicator]);
        this.changeDet.markForCheck();
      },
      () => {
        this.loading = false;
        this.avanoLoadingIndicatorService.unblock([this.blockingIndicator]);
      }
    );
  }
}
